/** Map PortfolioUpload GraphQL payload → SQUAD page-2 right-panel fields. */

export type LabelValueNum = { label: string; value: number };
export type LabelValueStr = { label: string; value: string };

export type SquadPortfolioTheme = {
  pieCharts?: { fund: string; colors: string[] }[];
  barCharts?: { fund: string; colors: string[] }[];
  fsHeader?: { fund: string; colors: string[] }[];
};

export type SquadPage2PortfolioFields = {
  allokationAktienLeft: LabelValueNum[];
  allokationAktienRight: LabelValueNum[];
  allocationFootnoteAktien: string;
  allokationEventsLeft: LabelValueNum[];
  allokationEventsRight: LabelValueNum[];
  allocationFootnoteEvents: string;
  assetAllocation: LabelValueNum[];
  marktkapitalisierung: LabelValueNum[];
  assetAllocationNote: string;
  /** Excel Farbtheme colors for Asset Allokation pie (widget-mode parity). */
  assetAllocationColors?: string[];
  /** Excel Farbtheme colors for Marktkapitalisierung pie. */
  marktkapitalisierungColors?: string[];
  /** Primary bar color from Excel Farbtheme (first bar color). */
  portfolioBarColor?: string;
  farbthemeFund?: string;
  renditenPortfoliodaten: {
    left: LabelValueStr[];
    right: LabelValueStr[];
    footnote: string;
  };
  /** Navy titles from Excel `sectionTitle` (same as client custom-portfolio). */
  sectionHeaders: Partial<{
    allokationAktien: string;
    allokationEvents: string;
    assetAllokation: string;
    renditenPortfoliodaten: string;
  }>;
};

type WidgetRow = {
  label?: string;
  value?: string;
  labelL?: string;
  isTemplateLastDataRow?: boolean;
};

type PortfolioWidget = {
  title?: string;
  art?: string;
  rows?: WidgetRow[];
  footnote?: string;
};

type PortfolioSection = {
  sectionTitle?: string;
  widgets?: PortfolioWidget[];
};

export function parseCustomPortfolioIsins(raw: string | string[] | undefined | null): string[] {
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x || '').trim().toUpperCase()).filter(Boolean);
  }
  if (typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
}

function parseNum(value: unknown): number {
  if (value == null) return 0;
  const s = String(value).replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(s.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function isEmptyArt(art?: string): boolean {
  return String(art ?? '').trim().toLowerCase() === 'empty';
}

function isTableArt(art?: string): boolean {
  const a = String(art ?? '').trim().toLowerCase();
  return a.includes('table') || a.includes('tabelle');
}

function isPieArt(art?: string): boolean {
  return String(art ?? '').trim().toLowerCase().includes('pie');
}

function isBarArt(art?: string): boolean {
  return String(art ?? '').trim().toLowerCase().includes('bar');
}

function artKind(art?: string): 'pie' | 'bar' | 'table' | 'empty' | 'other' {
  if (isEmptyArt(art)) return 'empty';
  if (isTableArt(art)) return 'table';
  if (isPieArt(art)) return 'pie';
  if (isBarArt(art)) return 'bar';
  return 'other';
}

function nonEmptyWidgets(section?: PortfolioSection): PortfolioWidget[] {
  return (section?.widgets || []).filter((w) => !isEmptyArt(w?.art));
}

function rowsToBarItems(rows?: WidgetRow[]): LabelValueNum[] {
  return (rows || [])
    .filter((r) => String(r?.label ?? '').trim() !== '' || String(r?.value ?? '').trim() !== '')
    .map((r) => ({
      label: String(r.label ?? '').trim(),
      value: parseNum(r.value),
    }));
}

function rowsToTableItems(rows?: WidgetRow[]): LabelValueStr[] {
  return (rows || [])
    .filter((r) => String(r?.label ?? '').trim() !== '' || String(r?.value ?? '').trim() !== '')
    .map((r) => ({
      label: String(r.label ?? '').trim(),
      value: String(r.value ?? '').trim(),
    }));
}

function sectionFootnote(widgets: PortfolioWidget[]): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const w of widgets) {
    const text = String(w?.footnote ?? '').trim();
    if (!text) continue;
    const key = text.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(text);
  }
  return parts.join('\n');
}

function classifySection(section: PortfolioSection): 'bar' | 'pie' | 'table' | 'unknown' {
  const kinds = nonEmptyWidgets(section).map((w) => artKind(w.art));
  if (kinds.some((k) => k === 'table')) return 'table';
  if (kinds.some((k) => k === 'pie')) return 'pie';
  if (kinds.some((k) => k === 'bar')) return 'bar';
  return 'unknown';
}

/**
 * Map Excel portfolio sections → SQUAD page-2 fields (incl. sectionTitle → navy headers).
 */
export function mapPortfolioUploadToSquadPage2(
  sections: PortfolioSection[] | null | undefined,
): SquadPage2PortfolioFields | null {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  const barSections = sections.filter((s) => classifySection(s) === 'bar');
  const pieSections = sections.filter((s) => classifySection(s) === 'pie');
  const tableSections = sections.filter((s) => classifySection(s) === 'table');

  const aktien = nonEmptyWidgets(barSections[0]);
  const eventsWidgets = barSections.length > 1 ? nonEmptyWidgets(barSections[1]) : [];

  const pies = nonEmptyWidgets(pieSections[0]);
  const tables = nonEmptyWidgets(tableSections[0]);

  const sectionHeaders: SquadPage2PortfolioFields['sectionHeaders'] = {
    // Always set — empty clears SQUAD defaults when Excel has no matching section
    allokationAktien: String(barSections[0]?.sectionTitle ?? '').trim(),
    allokationEvents: String(barSections[1]?.sectionTitle ?? '').trim(),
    assetAllokation: String(pieSections[0]?.sectionTitle ?? '').trim(),
    renditenPortfoliodaten: String(tableSections[0]?.sectionTitle ?? '').trim(),
  };

  return {
    allokationAktienLeft: rowsToBarItems(aktien[0]?.rows),
    allokationAktienRight: rowsToBarItems(aktien[1]?.rows),
    allocationFootnoteAktien: sectionFootnote(aktien),
    allokationEventsLeft: rowsToBarItems(eventsWidgets[0]?.rows),
    allokationEventsRight: rowsToBarItems(eventsWidgets[1]?.rows),
    allocationFootnoteEvents: sectionFootnote(eventsWidgets),
    assetAllocation: rowsToBarItems(pies[0]?.rows),
    marktkapitalisierung: rowsToBarItems(pies[1]?.rows),
    assetAllocationNote: sectionFootnote(pies),
    renditenPortfoliodaten: {
      left: rowsToTableItems(tables[0]?.rows),
      right: rowsToTableItems(tables[1]?.rows),
      footnote: sectionFootnote(tables),
    },
    sectionHeaders,
  };
}

export function parsePortfolioWidgetsData(raw: unknown): PortfolioSection[] | null {
  if (!raw) return null;
  try {
    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
}

export function parsePortfolioThemeData(raw: unknown): SquadPortfolioTheme | null {
  if (!raw) return null;
  try {
    const theme = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!theme || typeof theme !== 'object') return null;
    return theme as SquadPortfolioTheme;
  } catch {
    return null;
  }
}

/** Resolve color set: portfolio Farbtheme (B4) first, then widget title, then first config row. */
export function getPieColorsFromTheme(
  theme: SquadPortfolioTheme | null,
  portfolioFarbthemeFund: string,
  fallbackWidgetTitle: string,
): string[] | undefined {
  const list = theme?.pieCharts;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const keys = [portfolioFarbthemeFund, fallbackWidgetTitle]
    .map((k) => (k || '').trim().toLowerCase())
    .filter(Boolean);
  for (const key of keys) {
    const match = list.find((e) => (e.fund || '').trim().toLowerCase() === key);
    if (match && Array.isArray(match.colors) && match.colors.length > 0) return match.colors;
  }
  return list[0]?.colors;
}

export function getBarColorsFromTheme(
  theme: SquadPortfolioTheme | null,
  portfolioFarbthemeFund: string,
  fallbackWidgetTitle: string,
): string[] | undefined {
  const list = theme?.barCharts;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const keys = [portfolioFarbthemeFund, fallbackWidgetTitle]
    .map((k) => (k || '').trim().toLowerCase())
    .filter(Boolean);
  for (const key of keys) {
    const match = list.find((e) => (e.fund || '').trim().toLowerCase() === key);
    if (match && Array.isArray(match.colors) && match.colors.length > 0) return match.colors;
  }
  return list[0]?.colors;
}

/** Resolve Excel Farbtheme colors for SQUAD page-2 charts (same order as client widget). */
export function resolveSquadPortfolioChartColors(
  sections: PortfolioSection[] | null | undefined,
  theme: SquadPortfolioTheme | null,
  farbthemeFund: string,
): Pick<
  SquadPage2PortfolioFields,
  'assetAllocationColors' | 'marktkapitalisierungColors' | 'portfolioBarColor' | 'farbthemeFund'
> {
  const pieSections = (sections || []).filter((s) => classifySection(s) === 'pie');
  const barSections = (sections || []).filter((s) => classifySection(s) === 'bar');
  const pies = nonEmptyWidgets(pieSections[0]);
  const bars = nonEmptyWidgets(barSections[0]);
  const fund = String(farbthemeFund || '').trim();

  const assetAllocationColors = getPieColorsFromTheme(theme, fund, String(pies[0]?.title || ''));
  const marktkapitalisierungColors = getPieColorsFromTheme(theme, fund, String(pies[1]?.title || ''));
  const barPalette =
    getBarColorsFromTheme(theme, fund, String(bars[0]?.title || '')) ||
    getPieColorsFromTheme(theme, fund, String(bars[0]?.title || ''));
  const portfolioBarColor =
    Array.isArray(barPalette) && barPalette.length > 0 ? String(barPalette[0]) : undefined;

  return {
    ...(assetAllocationColors?.length ? { assetAllocationColors } : {}),
    ...(marktkapitalisierungColors?.length ? { marktkapitalisierungColors } : {}),
    ...(portfolioBarColor ? { portfolioBarColor } : {}),
    ...(fund ? { farbthemeFund: fund } : {}),
  };
}

/**
 * Excel "FS-Header colors" (primary, secondary). Secondary is gold #CFB483 on every fund.
 * Used for page header + Rollierende Wertentwicklung (brutto / netto).
 */
export const SQUAD_FS_HEADER_SECONDARY = '#CFB483';
const SQUAD_FS_HEADER_DEFAULT_PRIMARY = '#0C1C3D';
const LEGACY_GOLD = '#C4A35A';

export const SQUAD_FS_HEADER_PALETTE: Array<{
  label: string;
  keys: string[];
  primary: string;
  secondary: string;
}> = [
  { label: 'Green Balance', keys: ['green balance'], primary: '#91C33F', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: '4 Convertibles', keys: ['convertible', 'convertibles'], primary: '#66A8DE', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Special Situations', keys: ['special situation', 'special situations'], primary: '#0C1C3D', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Point Five', keys: ['point five', 'pointfive'], primary: '#17406D', secondary: SQUAD_FS_HEADER_SECONDARY },
  // = Excel-Vorlage SQUAD (config, FS-Header). War seit 5bb32cd #0C1C3D und überschrieb die Excel-Farbe.
  { label: 'Aguja & Aguja Bond', keys: ['aguja'], primary: '#333399', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Praemium', keys: ['praemium', 'prämium'], primary: '#1F4B8D', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Gallo & SEC', keys: ['gallo'], primary: '#034F49', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Makro', keys: ['makro'], primary: '#376092', secondary: SQUAD_FS_HEADER_SECONDARY },
  { label: 'Value & Growth', keys: ['value', 'growth'], primary: '#0C1C3D', secondary: SQUAD_FS_HEADER_SECONDARY },
];

function normalizeHex(value?: string | null): string | undefined {
  const hex = String(value || '').trim();
  if (!hex) return undefined;
  return hex.startsWith('#') ? hex : `#${hex}`;
}

function hexKey(value?: string | null): string {
  return String(normalizeHex(value) || '').toLowerCase();
}

function isLegacyGold(value?: string | null): boolean {
  return hexKey(value) === LEGACY_GOLD.toLowerCase();
}

function normalizeFundText(value?: string | null): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[ä]/g, 'ae')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function matchStaticFsHeader(query: string): { primary: string; secondary: string } | undefined {
  const hay = normalizeFundText(query);
  if (!hay) return undefined;
  return SQUAD_FS_HEADER_PALETTE.find((row) =>
    row.keys.some((key) => hay.includes(normalizeFundText(key))),
  );
}

function bestThemeFsHeader(
  list: Array<{ fund?: string; colors?: string[] }> | undefined,
  query: string,
): { primary?: string; secondary?: string } {
  if (!Array.isArray(list) || list.length === 0) return {};
  const hay = normalizeFundText(query);
  let matched = hay
    ? list.find((e) => normalizeFundText(e?.fund) === hay)
    : undefined;
  if (!matched && hay) {
    matched = list.find((e) => {
      const fund = normalizeFundText(e?.fund);
      return fund && (hay.includes(fund) || fund.includes(hay) || fund.split(' ').some((t) => t && hay.includes(t)));
    });
  }
  const colors = (matched || list[0])?.colors;
  if (!Array.isArray(colors) || colors.length === 0) return {};
  return {
    primary: typeof colors[0] === 'string' ? colors[0] : undefined,
    secondary: typeof colors[1] === 'string' ? colors[1] : undefined,
  };
}

/**
 * Resolve FS-Header colors for the SQUAD page header.
 * Excel "FS-Header colors" usually contains [primary, secondary, ...].
 */
export function resolveSquadFsHeaderColors(
  theme: SquadPortfolioTheme | null,
  fsHeaderFund: string,
): { primary?: string; secondary?: string } {
  return bestThemeFsHeader(theme?.fsHeader, fsHeaderFund);
}

/** Live FS-Header pair for header chrome + rolling brutto/netto bars.
 * Document pickers (`brandPrimary` / `brandSecondary`) win over Excel theme and the
 * static fund table — text/chrome must follow the configured FS-Header colors.
 */
export function resolveSquadFsHeaderPalette(input: {
  title?: string;
  farbthemeFund?: string;
  brandPrimary?: string;
  brandSecondary?: string;
  theme?: SquadPortfolioTheme | null;
}): { primary: string; secondary: string } {
  const query = [input.farbthemeFund, input.title].filter(Boolean).join(' ');
  const fromTheme = resolveSquadFsHeaderColors(input.theme || null, input.farbthemeFund || input.title || '');
  const fromTable = matchStaticFsHeader(query);

  const primary =
    normalizeHex(input.brandPrimary) ||
    normalizeHex(fromTheme.primary) ||
    normalizeHex(fromTable?.primary) ||
    SQUAD_FS_HEADER_DEFAULT_PRIMARY;

  const secondaryCandidate =
    normalizeHex(input.brandSecondary) ||
    normalizeHex(fromTheme.secondary) ||
    normalizeHex(fromTable?.secondary);

  const secondary =
    !secondaryCandidate || isLegacyGold(secondaryCandidate)
      ? SQUAD_FS_HEADER_SECONDARY
      : secondaryCandidate;

  return { primary, secondary };
}

/** Fund-name defaults only (ignores document pickers) — used to seed brand colors. */
export function suggestSquadFsHeaderPalette(input: {
  title?: string;
  farbthemeFund?: string;
  theme?: SquadPortfolioTheme | null;
}): { primary: string; secondary: string } {
  return resolveSquadFsHeaderPalette({
    title: input.title,
    farbthemeFund: input.farbthemeFund,
    theme: input.theme,
  });
}
